/**
 * @fileoverview Dialog for generating a new MCP personal access token.
 * @module features/mcp/components/GenerateTokenDialog
 */

import { useState, type ReactElement } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface GenerateTokenDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (name: string | null) => void
  isPending: boolean
}

/**
 * Modal that collects an optional name and triggers token issuance.
 * Parent owns the mutation; this component only collects input.
 */
export function GenerateTokenDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: GenerateTokenDialogProps): ReactElement {
  const [name, setName] = useState('')

  const handleOpenChange = (next: boolean): void => {
    // Reset the name field whenever the dialog closes so it's fresh on the next open.
    if (!next) setName('')
    onOpenChange(next)
  }

  const handleSubmit = (): void => {
    const trimmed = name.trim()
    onSubmit(trimmed.length === 0 ? null : trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate MCP token</DialogTitle>
          <DialogDescription>
            Issue a personal access token so Claude can read your data via MCP.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="mcp-token-name">Name (optional)</Label>
          <Input
            id="mcp-token-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Claude Desktop — laptop"
            maxLength={128}
            disabled={isPending}
          />
          <p className="text-xs text-muted-foreground">
            Helps you tell tokens apart on this page. You can revoke any token at any time.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Generating…' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
